import { useState, useCallback } from 'react';
import type {
  ConsentPreferences,
  CookieCategory,
  UseCookieConsentReturn,
} from '../CookieConsent.types';
import { DEFAULT_PREFERENCES, ALL_ACCEPTED, ALL_REJECTED } from '../config/cookie-consent.config';
import { getStoredConsent, setStoredConsent } from '../utils/cookie-utils';
import { updateGTMConsent, pushConsentEvent } from '../utils/gtm-consent';
import { AUTO_ACCEPT_COOKIE_CONSENT } from '@/shared/utils/constants';

let hasAutoAcceptedConsent = false;

const readStoredPreferences = (): ConsentPreferences | null => {
  const stored = getStoredConsent();
  if (!stored) return null;
  const { necessary, analytics, marketing } = stored;
  const preferences: ConsentPreferences = { necessary, analytics, marketing };
  updateGTMConsent(preferences);
  return preferences;
};

const readInitialPreferences = (): ConsentPreferences | null => {
  if (!AUTO_ACCEPT_COOKIE_CONSENT) return readStoredPreferences();

  if (!hasAutoAcceptedConsent) {
    setStoredConsent(ALL_ACCEPTED);
    updateGTMConsent(ALL_ACCEPTED);
    pushConsentEvent('accept_all');
    hasAutoAcceptedConsent = true;
  }

  return ALL_ACCEPTED;
};

export function useCookieConsent(): UseCookieConsentReturn {
  const [consent, setConsent] = useState<ConsentPreferences | null>(readInitialPreferences);
  const [showBanner, setShowBanner] = useState(
    () => !AUTO_ACCEPT_COOKIE_CONSENT && consent === null
  );
  const [isCustomizing, setIsCustomizing] = useState(false);
  const [customPreferences, setCustomPreferences] =
    useState<ConsentPreferences>(DEFAULT_PREFERENCES);

  const saveConsent = useCallback((preferences: ConsentPreferences, action: string) => {
    setStoredConsent(preferences);
    setConsent(preferences);
    updateGTMConsent(preferences);
    pushConsentEvent(action);
    setShowBanner(false);
    setIsCustomizing(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent(ALL_ACCEPTED, 'accept_all');
  }, [saveConsent]);

  const rejectAll = useCallback(() => {
    saveConsent(ALL_REJECTED, 'reject_all');
  }, [saveConsent]);

  const toggleCustomize = useCallback(() => {
    setIsCustomizing(prev => !prev);
  }, []);

  const updateCategory = useCallback((category: CookieCategory, enabled: boolean) => {
    if (category === 'necessary') return;
    setCustomPreferences(prev => ({ ...prev, [category]: enabled }));
  }, []);

  const saveCustom = useCallback(() => {
    saveConsent(customPreferences, 'save_custom');
  }, [customPreferences, saveConsent]);

  return {
    showBanner,
    consent,
    isCustomizing,
    customPreferences,
    acceptAll,
    rejectAll,
    toggleCustomize,
    updateCategory,
    saveCustom,
  };
}
